/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { restructureResponseForCheckerTable } from '../../utils/commonMethods/generalMethods';
import { updateCheckedForClf } from '../../utils/commonMethods/generalMethodsExtended';
import { getPaginatedChunkData, getReasonStatusMapping } from '../../utils/commonUtil';
import { CHECKER_DETAILS_INITIAL_STATE } from './constants';

export const setCheckerDetailsTableDataFulfilled = (state: CheckerDetailsTableStateType, action: CheckerDetailsTableAction) => {
  const calculatedPageNum = (action.payload.records_offset + state.paginationData.rowPerPage) / state.paginationData.rowPerPage;
  const reasonStatusMapping = getReasonStatusMapping();
  const restructuredData = restructureResponseForCheckerTable(action.payload.result, reasonStatusMapping);
  const prevPage = state.paginationData.page;
  const chunkData = getPaginatedChunkData(
    calculatedPageNum,
    restructuredData.tableData,
    state.paginationData.rowPerPage,
    prevPage,
    action.payload.records_offset,
    state.total_records,
  );
  const clfState = { ...state.checkerTableClfState };
  if (action?.meta?.arg?.operation === 'columnLevelFilter') {
    const filterObject = action?.meta.arg.selectedTableFilters;
    Object.entries(filterObject).forEach(([key, value]) => {
      clfState[key as keyof checkerTableClfType] = updateCheckedForClf(clfState[key as keyof checkerTableClfType], value as string[]);
    });
  }
  return {
    ...state,
    isCheckerDetailsDataLoading: false,
    checkerDetailsData: restructuredData,
    total_records: action.payload.result.total_result,
    checkerTableClfState: clfState,
    errorMessage: '',
    // // update the pagination config as per response count
    paginationData: {
      start: action.payload.records_offset,
      end: action.payload.records_offset + action.payload.result.ACR_MIS_data.length,
      rowPerPage: state.paginationData.rowPerPage,
      page: calculatedPageNum, // currently selected
      selectedChunkData: chunkData,
    },
  };
};

export const setCheckerDetailsTableDataPending = (state: CheckerDetailsTableStateType) => ({
  ...state,
  isCheckerDetailsDataLoading: true,
});

export const setCheckerDetailsTableDataRejected = (state: CheckerDetailsTableStateType, action?:FilterAPIRejectedAction) => {
  // if request comes from column level filter and no record found for the combination we are showing the previous state
  /* istanbul ignore next */
  if (action?.meta?.arg?.operation === 'columnLevelFilter') {
    /* istanbul ignore next */
    return {
      ...state,
      isCheckerDetailsDataLoading: false,
      errorMessage: 'No data found for the selected filter combination',
    };
  }
  if (action?.meta.arg === 'columnLevelFilter') {
    return { ...state, isCheckerDetailsDataLoading: false };
  }
  return {
    ...state,
    isCheckerDetailsDataLoading: false,
    errorMessage: 'Table Data Failed',
    checkerDetailsData: CHECKER_DETAILS_INITIAL_STATE.checkerDetailsData,
    paginationData: CHECKER_DETAILS_INITIAL_STATE.paginationData,
    total_records: CHECKER_DETAILS_INITIAL_STATE.total_records,
    records_offset: CHECKER_DETAILS_INITIAL_STATE.records_offset,
  };
};

// load fleet fin and ata together
export const loadCheckerFleetFinAndAtaDetailsPending = (state: CheckerDetailsTableStateType) => ({
  ...state,
  isFilterDetailsFinLoading: true,
});

export const loadCheckerFleetFinAndAtaDetailsRejected = (state: CheckerDetailsTableStateType, action: CheckerDetailsTableAction) => {
  return {
    ...state,
    isFilterDetailsFinLoading: false,
    errorMessage: action.payload?.message,
    fleetDetailFilterOptions: {
      ...state.fleetDetailFilterOptions,
    },
  };
};

export const loadCheckerFleetFinAndAtaDetailsFulfilled = (state: CheckerDetailsTableStateType, action: CheckerDetailsTableAction) => {
  const FleetAtaResp = action.payload?.result?.ACR_MIS_data.map((item) => item.fleet_data.ata)
    .filter((item: any, index: number, array: any) => array.indexOf(item) === index && item !== 'ZZZZ');
  const rawFleetFins = action.payload?.result?.ACR_MIS_data.map((item) => item.fleet_data.fleet_fin) || [];

  // Remove duplicates by reducing into an accumulator array
  const uniqueFins = rawFleetFins?.reduce<string[]>((acc:any, fin) => {
    if (!acc.includes(fin)) acc.push(fin);
    return acc;
  }, []);

  // Separate ZZZZ_ and normal fins immutably
  const noFinExists = uniqueFins?.some((fin: any) => fin && fin?.startsWith('ZZZZ_'));
  const filteredFins = uniqueFins?.filter((fin: any) => !fin?.startsWith('ZZZZ_'));

  // Compose final array immutably
  const FleetFinResp = noFinExists ? ['No FIN', ...filteredFins] : filteredFins;

  const finObjects = (uniqueFins ?? [])
    .filter((fin): fin is string => !!fin)
    .map((fin) => {
      // If value starts with ZZZZ_, return 'No FIN', else return the value as is
      if (fin.startsWith('ZZZZ_')) {
        return 'No FIN';
      }
      return fin;
    })
    .filter((fin, idx, arr) => arr.indexOf(fin) === idx); // Remove duplicates

  const sortedFinObjects = finObjects.includes('No FIN') ? ['No FIN', ...finObjects.filter((fin) => fin !== 'No FIN')] : finObjects;

  const finObjectsFinal = sortedFinObjects.reduce((acc: Record<string, boolean>, fin: string) => {
    acc[fin] = false;
    return acc;
  }, {});

  const ataArrayOrdered = ['None / Multiple values', ...(FleetAtaResp ?? [])];
  const ataObjects = (ataArrayOrdered ?? [])
    .filter((ata): ata is string => !!ata)
    .reduce((acc: Record<string, boolean>, ata: string) => {
      acc[ata] = false;
      return acc;
    }, {});

  if (action.meta?.arg === 'CheckerDetailsOperatorChanged') {
    return {
      ...state,
      isFilterDetailsFinLoading: false,
      checkerDetailsFilterOptions: {
        ...state.checkerDetailsFilterOptions,
        fin: FleetFinResp,
        ata: FleetAtaResp?.filter((ata) => !!ata),
      },
      checkerTableDataSelection: {
        ...state.checkerTableDataSelection,
        fin: finObjectsFinal,
        ata: ataObjects,
      },
      checkerTableClfState: {
        ...state.checkerTableClfState,
        fleet_fin: finObjectsFinal,
        ata: ataObjects,
      },
    };
  }
  return {
    ...state,
    isFilterDetailsFinLoading: false,
    fleetDetailFilterOptions: {
      ...state.fleetDetailFilterOptions,
      fin: FleetFinResp,
      ata: FleetAtaResp?.filter((ata) => !!ata),
    },
    checkerTableDataSelection: {
      ...state.checkerTableDataSelection,
      fin: finObjectsFinal,
      ata: ataObjects,
    },
    checkerTableClfState: {
      ...state.checkerTableClfState,
      fleet_fin: finObjectsFinal,
      ata: ataObjects,
    },
  };
};
